import { useMemo, useState, type FormEvent } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { confirmPasswordReset } from "@/services/auth";
import { AequiaMark, Button, ErrorBanner, Input } from "@/components/ui";

export default function PasswordResetConfirmPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(
    () => params.token ?? search.get("token") ?? "",
    [params.token, search],
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("La confirmación no coincide.");
      return;
    }
    setLoading(true);
    try {
      await confirmPasswordReset(token, password, confirm);
      navigate("/login", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudo restablecer la contraseña",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg grid place-items-center p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-[420px] rounded-xl border border-border bg-surface p-7 shadow-subtle"
      >
        <AequiaMark size={72} className="mb-6" />
        <div className="text-[11px] uppercase tracking-eyebrow text-muted font-semibold mb-2">
          Nueva contraseña
        </div>
        <h1 className="font-serif text-3xl font-medium tracking-tightish mb-2">
          Define una contraseña segura
        </h1>
        <p className="text-sm text-muted mb-6">
          Usa una contraseña nueva. El sistema rechazará contraseñas recientes o
          que no cumplan la política vigente.
        </p>
        {!token && (
          <div className="mb-4">
            <ErrorBanner
              title="No se pudo restablecer la contraseña"
              message="El enlace de recuperación no contiene un token válido."
            />
          </div>
        )}
        <Input
          label="Nueva contraseña"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
        />
        <Input
          label="Confirmar contraseña"
          type="password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          required
        />
        {error && (
          <div className="mb-3">
            <ErrorBanner
              title="No se pudo restablecer la contraseña"
              message={error}
            />
          </div>
        )}
        <Button
          type="submit"
          variant="brand"
          size="lg"
          full
          loading={loading}
          disabled={!token}
        >
          Cambiar contraseña
        </Button>
        <Link
          to="/login"
          className="block text-center text-xs text-muted hover:text-text mt-5"
        >
          Volver al inicio de sesión
        </Link>
      </form>
    </div>
  );
}
